import React, { useMemo, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuCircleCheck, LuCircleX, LuLoaderPinwheel } from 'react-icons/lu';

import { WizardProposal, WizardProposalTask, WizardTaskBuildProgress } from '../../entities/wizard-proposal';

interface WizardProposalPanelProps {
  proposal: WizardProposal;
  taskProgress: Record<string, WizardTaskBuildProgress>;
  onQuickAction: (text: string) => void;
  onRetry: () => void;
  isRetrying: boolean;
  // When the platform was already chosen upstream (e.g. onboarding's platform-first step), only
  // that one button is worth showing — no need to re-ask. Omit to show all three.
  presetPlatform?: 'jira' | 'trello' | 'standalone';
}

const STATUS_LABEL: Record<WizardProposal['status'], string> = {
  draft: 'Draft — reviewable',
  confirmed: 'Confirmed — starting build…',
  building: 'Building…',
  built: 'Built',
  failed: 'Build failed',
};

const STATUS_BADGE: Record<WizardProposal['status'], string> = {
  draft: 'badge-ghost',
  confirmed: 'badge-info',
  building: 'badge-info',
  built: 'badge-success',
  failed: 'badge-error',
};

const PLATFORMS: { id: 'jira' | 'trello' | 'standalone'; label: string; prompt: string }[] = [
  { id: 'jira', label: 'Build on Jira', prompt: 'Build this on Jira.' },
  { id: 'trello', label: 'Build on Trello', prompt: 'Build this on Trello.' },
  { id: 'standalone', label: 'Build in mFlow', prompt: 'Build this in standalone mFlow.' },
];

const TaskProgressIcon: React.FC<{ status?: WizardTaskBuildProgress['status'] }> = ({ status }) => {
  if (status === 'created') return <LuCircleCheck className="text-success shrink-0" size={16} />;
  if (status === 'failed') return <LuCircleX className="text-error shrink-0" size={16} />;
  if (status === 'creating') return <LuLoaderPinwheel className="animate-spin opacity-70 shrink-0" size={16} />;
  return <span className="w-4 h-4 rounded-full border border-base-300 shrink-0" />;
};

interface TaskNode extends WizardProposalTask {
  children: TaskNode[];
}

// tasks[] from the server is always flat (parentId is just a reference, see wizard-contract.md
// §7) — build the tree client-side purely for rendering. Jira-only for now; Trello/Standalone
// tasks never carry a parentId today, so this collapses to the same flat list for them.
function buildTaskTree(tasks: WizardProposalTask[]): TaskNode[] {
  const byId = new Map<string, TaskNode>();
  tasks.forEach((t) => byId.set(t.id, { ...t, children: [] }));
  const roots: TaskNode[] = [];
  byId.forEach((node) => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  return roots;
}

const TaskRow: React.FC<{
  node: TaskNode;
  taskProgress: Record<string, WizardTaskBuildProgress>;
  showProgress: boolean;
}> = ({ node, taskProgress, showProgress }) => {
  // Live WS progress wins while connected; `buildStatus` is the reload-safe fallback (survives a
  // page refresh, since taskProgress resets to empty then).
  const status = taskProgress[node.id]?.status ?? node.buildStatus;
  const error = taskProgress[node.id]?.error ?? node.buildError;
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2 text-sm bg-base-100 rounded-lg px-3 py-2">
        {showProgress && <TaskProgressIcon status={status} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {node.type && <span className="badge badge-outline badge-xs">{node.type}</span>}
            <span className="truncate">{node.title}</span>
            {node.targetId && <span className="font-mono text-[10px] opacity-50">{node.targetId}</span>}
          </div>
          {status === 'failed' && error && <div className="text-xs text-error mt-0.5">{error}</div>}
        </div>
      </div>
      {node.children.length > 0 && (
        <div className="ml-5 pl-3 border-l border-base-300 space-y-1">
          {node.children.map((child) => (
            <TaskRow key={child.id} node={child} taskProgress={taskProgress} showProgress={showProgress} />
          ))}
        </div>
      )}
    </div>
  );
};

const WizardProposalPanel: React.FC<WizardProposalPanelProps> = ({
  proposal,
  taskProgress,
  onQuickAction,
  onRetry,
  isRetrying,
  presetPlatform,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const isBuildPhase = proposal.status === 'confirmed' || proposal.status === 'building';
  const isDraft = proposal.status === 'draft';
  const visiblePlatforms = presetPlatform ? PLATFORMS.filter((p) => p.id === presetPlatform) : PLATFORMS;
  // proposal.buildError is only set for a whole-build-level failure — a "built" proposal where
  // most tasks succeeded but one didn't leaves it unset, so check individual tasks too or the
  // retry button never shows for partial failures.
  const hasFailedTask = proposal.tasks.some((task) => (taskProgress[task.id]?.status ?? task.buildStatus) === 'failed');
  const taskTree = useMemo(() => buildTaskTree(proposal.tasks), [proposal.tasks]);
  const showProgress = isBuildPhase || proposal.status === 'built' || proposal.status === 'failed';

  return (
    <div className="rounded-xl bg-base-200 shadow-card overflow-hidden">
      <button
        className="w-full flex items-center justify-between gap-2 px-4 py-2 text-left"
        onClick={() => setCollapsed((v) => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium truncate">{proposal.projectName || 'New project proposal'}</span>
          <span className={`badge badge-sm ${STATUS_BADGE[proposal.status]}`}>{STATUS_LABEL[proposal.status]}</span>
        </div>
        {collapsed ? <LuChevronDown size={16} /> : <LuChevronUp size={16} />}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-3 max-h-[55vh] overflow-y-auto">
          {proposal.methodology && (
            <div className="text-sm">
              <span className="font-medium capitalize">{proposal.methodology}</span>
              {proposal.methodologyRationale && <span className="opacity-70"> — {proposal.methodologyRationale}</span>}
            </div>
          )}

          {proposal.tasks.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold opacity-50 uppercase tracking-wide">
                Tasks ({proposal.tasks.length})
              </div>
              {isDraft && (
                <div className="text-xs opacity-40">
                  Tip: ask to add subtasks — e.g. &quot;add a subtask to task 3 for writing tests&quot;.
                </div>
              )}
              <div className="space-y-1">
                {taskTree.map((node) => (
                  <TaskRow key={node.id} node={node} taskProgress={taskProgress} showProgress={showProgress} />
                ))}
              </div>
            </div>
          )}

          {proposal.docs.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold opacity-50 uppercase tracking-wide">
                Docs ({proposal.docs.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {proposal.docs.map((doc) => (
                  <span key={doc.id} className="badge badge-outline badge-sm" title={doc.content}>
                    {doc.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {isDraft && (
            <div className="space-y-1 pt-1">
              <div className="text-xs font-semibold opacity-50 uppercase tracking-wide">
                Review above, then edit by chatting, or pick a platform
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {visiblePlatforms.map((p) => {
                  // With no preset, Standalone is the only option that needs zero setup — flag it
                  // as the recommended default rather than presenting three equal choices.
                  const isRecommended = !presetPlatform && p.id === 'standalone';
                  return (
                    <button
                      key={p.id}
                      className={`btn btn-sm ${isRecommended ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => onQuickAction(p.prompt)}
                    >
                      {p.label}
                    </button>
                  );
                })}
                {!presetPlatform && <span className="badge badge-ghost badge-sm">mFlow needs no setup</span>}
              </div>
            </div>
          )}

          {proposal.status === 'failed' && (
            <div className="alert alert-error text-sm flex items-center justify-between gap-2">
              <span>{proposal.buildError}</span>
              <button className="btn btn-error btn-sm shrink-0" onClick={onRetry} disabled={isRetrying}>
                {isRetrying && <LuLoaderPinwheel className="animate-spin" size={14} />}
                Retry failed tasks
              </button>
            </div>
          )}

          {proposal.status === 'built' && (
            <div className="alert alert-success text-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <span>
                  Built successfully
                  {proposal.builtProjectId ? ` — ${proposal.builtProjectId}` : ''}
                  {proposal.builtListId ? ` (list ${proposal.builtListId})` : ''}.
                  {proposal.buildError ? ` Note: ${proposal.buildError}` : ''}
                </span>
                {(proposal.buildError || hasFailedTask) && (
                  <button className="btn btn-outline btn-sm shrink-0" onClick={onRetry} disabled={isRetrying}>
                    {isRetrying && <LuLoaderPinwheel className="animate-spin" size={14} />}
                    Retry failed tasks
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WizardProposalPanel;
